import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  DurumRozeti,
  Iskelet,
  Kart,
  MetinAlani,
  OnayKutusu,
  Rozet,
  Sekmeler,
  type Sekme,
  type UnkDurum,
} from "@koclukpanel/ui";
import { ApiHatasi } from "./api";
import {
  siparisDurumuGuncelle,
  siparisKagitOnayi,
  yonetimSiparisi,
  yonetimSiparisIptal,
  yonetimSiparisleri,
} from "./api-magaza";
import { PANEL_IPTAL_NEDENI_ETIKETI, SIPARIS_DURUMU_TONU } from "./magaza-etiketleri";
import {
  SIPARIS_DURUMU_ETIKETI,
  type SiparisAdresi,
  type SiparisDurumu,
  type SiparisKuyrukDurumu,
  type YonetimSiparisDetayi,
  type YonetimSiparisi,
} from "./magaza-tipleri";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * SİPARİŞ YÖNETİMİ — F-GAM-17, oyunlaştırma dalga 2 / sürüm 7a. İzin: `oyun.siparis.yonet` (ekranı çağıran karar verir).
 *
 * Sipariş kuyruğu: durum süzgeci, satır ayrıntısı (ADRES YALNIZ BURADA; liste hiç adres taşımaz), kargoya verme
 * (firma + takip no zorunlu), teslim, iptal (gerekçe zorunlu; puan iade + stok açılır, aynı işlemde SUNUCUDA).
 * Kâğıt veli onayı (Emre S11): portal velisi olmayan 18 altı için yönetici imzalı onayın taramasını ve adresi yükleyip
 * siparişi onaylar; sudo ister (`kvkk.riza.kagit_kaydet` kalıbı). Hangi geçişin geçerli olduğu, iade ve stok SUNUCUDA:
 * ekran yalnız duruma uygun düğmeyi gösterir.
 */

const SUZGEC: readonly Sekme<SiparisKuyrukDurumu>[] = [
  { deger: "TUMU", etiket: "Tümü" },
  { deger: "AWAITING_PARENT", etiket: "Veli bekliyor" },
  { deger: "AWAITING_ADDRESS", etiket: "Adres bekliyor" },
  { deger: "PREPARING", etiket: "Hazırlanıyor" },
  { deger: "SHIPPED", etiket: "Kargoda" },
  { deger: "DELIVERED", etiket: "Teslim" },
  { deger: "CANCELLED", etiket: "İptal" },
];

function hataYazisi(e: unknown, varsayilan: string): string {
  return e instanceof ApiHatasi ? e.message : varsayilan;
}

/** Sudo hatası kapıya gider, ekranda ayrıca hata yazılmaz (kapı zaten şifre sorar). */
function sudoMu(e: unknown): boolean {
  return e instanceof ApiHatasi && e.sudoGerekliMi;
}

function kagitOnayBekliyorMu(
  s: Pick<YonetimSiparisi, "kagitOnayiGerekli" | "kagitOnayli" | "durum">,
) {
  return (
    s.kagitOnayiGerekli && !s.kagitOnayli && s.durum !== "CANCELLED" && s.durum !== "DELIVERED"
  );
}

/*
  UNİKOÇ dört durum rengi (tarama-2 §2.13): bekleyen turuncu, yolda mavi, teslim yeşil.
  İptal için dördüncü bir renk yok → gri Rozet'te kalıyor (renk uydurulmuyor).
*/
const SIPARIS_DURUMU_RENGI: Partial<Record<SiparisDurumu, UnkDurum>> = {
  AWAITING_PARENT: "bekliyor",
  AWAITING_ADDRESS: "bekliyor",
  PREPARING: "planli",
  SHIPPED: "planli",
  DELIVERED: "tamamlandi",
};

export function SiparisYonetimi({ kagitOnayVerebilir }: { kagitOnayVerebilir: boolean }) {
  const [suzgec, setSuzgec] = useState<SiparisKuyrukDurumu>("TUMU");
  const [acik, setAcik] = useState<string | null>(null);
  // İmleçli sayfalar (Codex 7A-04): yeni kayıtlar eski işleri listenin dışına itmesin.
  const q = useInfiniteQuery({
    queryKey: ["magaza-siparisleri", suzgec],
    queryFn: ({ pageParam }) => yonetimSiparisleri(suzgec, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (son) => son.sonraki ?? undefined,
    retry: false,
  });
  const satirlar = q.data?.pages.flatMap((p) => p.satirlar) ?? [];
  return (
    <Kart
      baslik="Sipariş kuyruğu"
      aciklama="Öğrencilerin mağaza siparişleri. Adres yalnız sipariş ayrıntısında görünür."
    >
      <Sekmeler
        etiket="Sipariş durumu"
        sekmeler={SUZGEC}
        secili={suzgec}
        onSec={(d) => {
          setSuzgec(d);
          setAcik(null);
        }}
        className="mb-3"
      />
      {q.isPending ? (
        <Iskelet satir={4} />
      ) : q.error && !q.data ? (
        // Yalniz ILK yukleme hatasi listenin yerini alir (Codex 7A-T2-04); devam sayfasi hatasi yuklu listeyi gizlemez.
        <p role="alert" className="text-hata-700 text-sm">
          Siparişler alınamadı.
        </p>
      ) : satirlar.length === 0 ? (
        <p className="text-notr-600 text-sm">Bu durumda sipariş yok.</p>
      ) : (
        <>
          <ul className="divide-notr-100 divide-y text-sm">
            {satirlar.map((s) => (
              <li key={s.id} className="flex flex-col gap-2 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-col gap-0.5">
                    <p className="text-notr-900 font-medium">
                      {s.ogrenciAdi} · {s.urunAdi}
                    </p>
                    <p className="text-notr-600 text-xs">
                      {s.tarih.slice(0, 10)} · <span className="tabular-nums">{s.puan}</span> puan
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {SIPARIS_DURUMU_RENGI[s.durum] ? (
                      <DurumRozeti durum={SIPARIS_DURUMU_RENGI[s.durum] as UnkDurum}>
                        {SIPARIS_DURUMU_ETIKETI[s.durum]}
                      </DurumRozeti>
                    ) : (
                      <Rozet ton={SIPARIS_DURUMU_TONU[s.durum]}>
                        {SIPARIS_DURUMU_ETIKETI[s.durum]}
                      </Rozet>
                    )}
                    {kagitOnayBekliyorMu(s) && <Rozet ton="uyari">Kâğıt onayı bekliyor</Rozet>}
                    <Buton
                      gorunum="sessiz"
                      aria-expanded={acik === s.id}
                      aria-label={`${s.ogrenciAdi} · ${s.urunAdi} siparişinin ayrıntısı`}
                      onClick={() => setAcik(acik === s.id ? null : s.id)}
                    >
                      {acik === s.id ? "Kapat" : "Ayrıntı"}
                    </Buton>
                  </div>
                </div>
                {acik === s.id && (
                  <SiparisDetayi id={s.id} kagitOnayVerebilir={kagitOnayVerebilir} />
                )}
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="flex flex-col items-start gap-2 pt-3">
              <p role="status" className="text-notr-700 text-sm">
                {satirlar.length} sipariş gösteriliyor; daha eski siparişler var.
              </p>
              <Buton
                gorunum="ikincil"
                yukleniyor={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                Daha fazla yükle
              </Buton>
            </div>
          )}
          {q.isFetchNextPageError && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <p role="alert" className="text-hata-700 text-sm">
                Devam sayfası alınamadı.
              </p>
              <Buton
                gorunum="ikincil"
                yukleniyor={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                Yeniden dene
              </Buton>
            </div>
          )}
        </>
      )}
    </Kart>
  );
}

function SiparisDetayi({ id, kagitOnayVerebilir }: { id: string; kagitOnayVerebilir: boolean }) {
  const q = useQuery({
    queryKey: ["magaza-siparis", id],
    queryFn: () => yonetimSiparisi(id),
    retry: false,
  });
  if (q.isPending) return <Iskelet satir={3} />;
  if (q.error)
    return (
      <p role="alert" className="text-hata-700 text-sm">
        Sipariş ayrıntısı alınamadı.
      </p>
    );
  return <DetayGovdesi s={q.data} kagitOnayVerebilir={kagitOnayVerebilir} />;
}

const ADRES_VEREN: Record<"VELI" | "OGRENCI" | "YONETICI", string> = {
  VELI: "veli",
  OGRENCI: "öğrenci",
  YONETICI: "yönetici (kâğıt onay)",
};

function DetayGovdesi({
  s,
  kagitOnayVerebilir,
}: {
  s: YonetimSiparisDetayi;
  kagitOnayVerebilir: boolean;
}) {
  const qc = useQueryClient();
  const [kargoFirmasi, setKargoFirmasi] = useState("");
  const [takipNo, setTakipNo] = useState("");
  const [iptalAcik, setIptalAcik] = useState(false);
  const [kagitAcik, setKagitAcik] = useState(false);

  const yenile = () => {
    void qc.invalidateQueries({ queryKey: ["magaza-siparisleri"] });
    void qc.invalidateQueries({ queryKey: ["magaza-siparis", s.id] });
  };
  const kargola = useMutation({
    mutationFn: () =>
      siparisDurumuGuncelle(s.id, {
        durum: "SHIPPED",
        kargoFirmasi: kargoFirmasi.trim(),
        takipNo: takipNo.trim(),
      }),
    onSuccess: yenile,
  });
  const teslim = useMutation({
    mutationFn: () => siparisDurumuGuncelle(s.id, { durum: "DELIVERED" }),
    onSuccess: yenile,
  });

  const iptalEdilebilir = s.durum !== "DELIVERED" && s.durum !== "CANCELLED";
  const kagitBekliyor = kagitOnayBekliyorMu(s);
  const kargoHazir = kargoFirmasi.trim().length > 0 && takipNo.trim().length > 0;

  return (
    <div className="bg-notr-50 rounded-kart flex flex-col gap-3 p-3" data-testid="siparis-detayi">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        <Satir etiket="Öğrenci" deger={s.ogrenciAdi} />
        <Satir etiket="Ürün" deger={`${s.urunAdi} · ${s.puan} puan`} />
        <Satir etiket="Durum" deger={SIPARIS_DURUMU_ETIKETI[s.durum]} />
        {s.sonGun && <Satir etiket="Onay için son gün" deger={s.sonGun.slice(0, 10)} />}
        {s.kargoFirmasi && <Satir etiket="Kargo firması" deger={s.kargoFirmasi} />}
        {s.takipNo && <Satir etiket="Takip numarası" deger={s.takipNo} />}
        {s.kargoTarihi && <Satir etiket="Kargoya verildi" deger={s.kargoTarihi.slice(0, 10)} />}
        {s.teslimTarihi && <Satir etiket="Teslim edildi" deger={s.teslimTarihi.slice(0, 10)} />}
        {s.iptalNedeni && (
          <Satir
            etiket="İptal nedeni"
            deger={`${PANEL_IPTAL_NEDENI_ETIKETI[s.iptalNedeni]}${s.iptalTarihi ? ` · ${s.iptalTarihi.slice(0, 10)}` : ""}`}
          />
        )}
        {(s.kagitOnayiGerekli || s.kagitOnayli) && (
          <Satir
            etiket="Kâğıt veli onayı"
            deger={s.kagitOnayli ? "Alındı" : "Bekleniyor (portal velisi yok)"}
          />
        )}
      </dl>

      <div className="flex flex-col gap-1">
        <h4 className="text-notr-900 text-sm font-medium">Teslimat adresi</h4>
        {s.adres === null ? (
          <p className="text-notr-600 text-sm">
            Adres silindi ya da henüz girilmedi. (Adres teslim ya da iptalden 6 ay sonra silinir.)
          </p>
        ) : (
          <address className="text-notr-900 text-sm not-italic">
            <p>{s.adres.aliciAdi}</p>
            <p>{s.adres.telefon}</p>
            <p>
              {s.adres.adres}, {s.adres.ilce} / {s.adres.il}
              {s.adres.postaKodu ? ` · ${s.adres.postaKodu}` : ""}
            </p>
            <p className="text-notr-600 text-xs">
              Adresi veren: {s.adresiVeren ? ADRES_VEREN[s.adresiVeren] : "—"}
            </p>
          </address>
        )}
      </div>

      {kagitBekliyor && kagitOnayVerebilir && !kagitAcik && (
        <div>
          <Buton onClick={() => setKagitAcik(true)}>Kâğıt onayı gir</Buton>
        </div>
      )}
      {kagitBekliyor && kagitAcik && kagitOnayVerebilir && (
        <KagitOnayFormu siparisId={s.id} onKapat={() => setKagitAcik(false)} onBitti={yenile} />
      )}

      {s.durum === "PREPARING" && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (kargoHazir && !kargola.isPending) kargola.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Alan
              etiket="Kargo firması"
              zorunlu
              maxLength={80}
              value={kargoFirmasi}
              onChange={(e) => setKargoFirmasi(e.target.value)}
            />
            <Alan
              etiket="Takip numarası"
              zorunlu
              maxLength={80}
              value={takipNo}
              onChange={(e) => setTakipNo(e.target.value)}
            />
          </div>
          {kargola.isError && (
            <p role="alert" className="text-hata-700 text-xs">
              {hataYazisi(kargola.error, "Sipariş kargoya verilemedi.")}
            </p>
          )}
          <div>
            <Buton type="submit" disabled={!kargoHazir} yukleniyor={kargola.isPending}>
              Kargoya ver
            </Buton>
          </div>
        </form>
      )}

      {s.durum === "SHIPPED" && (
        <div className="flex flex-col gap-2">
          {teslim.isError && (
            <p role="alert" className="text-hata-700 text-xs">
              {hataYazisi(teslim.error, "Durum güncellenemedi.")}
            </p>
          )}
          <div>
            <Buton yukleniyor={teslim.isPending} onClick={() => teslim.mutate()}>
              Teslim edildi olarak işaretle
            </Buton>
          </div>
        </div>
      )}

      {iptalEdilebilir && !iptalAcik && (
        <div>
          <Buton gorunum="tehlike" onClick={() => setIptalAcik(true)}>
            Siparişi iptal et
          </Buton>
        </div>
      )}
      {iptalEdilebilir && iptalAcik && (
        <IptalFormu
          siparisId={s.id}
          kargoda={s.durum === "SHIPPED"}
          onKapat={() => setIptalAcik(false)}
          onBitti={yenile}
        />
      )}
    </div>
  );
}

function Satir({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div className="flex gap-1">
      <dt className="text-notr-600">{etiket}:</dt>
      <dd className="text-notr-900">{deger}</dd>
    </div>
  );
}

function IptalFormu({
  siparisId,
  kargoda,
  onKapat,
  onBitti,
}: {
  siparisId: string;
  kargoda: boolean;
  onKapat: () => void;
  onBitti: () => void;
}) {
  const [teslimEdilemedi, setTeslimEdilemedi] = useState(false);
  // Serbest gerekce YOK (sozlesme `yonetimIptalSemasi`, tasarim §1.3): iptal nedeni enum olarak denetime yazilir.
  // Kargodaki sipariste yalniz "teslim edilemedi" (S6) gecerli; sunucu baska nedeni reddeder.
  const mut = useMutation({
    mutationFn: () =>
      yonetimSiparisIptal(siparisId, {
        neden: kargoda ? "UNDELIVERABLE" : "ADMIN",
      }),
    onSuccess: () => {
      onBitti();
      onKapat();
    },
  });
  const gecerli = !kargoda || teslimEdilemedi;
  return (
    <form
      className="border-notr-200 flex flex-col gap-2 border-t pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (gecerli && !mut.isPending) mut.mutate();
      }}
    >
      <p className="text-notr-700 text-sm">
        İptal edilince sipariş puanı öğrenciye iade edilir ve ürün stoğu yeniden açılır. Bu işlem
        geri alınamaz.
      </p>
      {kargoda && (
        <OnayKutusu
          etiket="Kargo teslim edemedi (teslim edilemedi → iade)"
          yardim="Kargodaki sipariş yalnız teslim edilemediyse iptal edilir; puan aynı işlemde iade edilir ve stok döner."
          checked={teslimEdilemedi}
          onChange={(e) => setTeslimEdilemedi(e.target.checked)}
        />
      )}
      {mut.isError && (
        <p role="alert" className="text-hata-700 text-xs">
          {hataYazisi(mut.error, "Sipariş iptal edilemedi.")}
        </p>
      )}
      <div className="flex gap-2">
        <Buton type="submit" gorunum="tehlike" disabled={!gecerli} yukleniyor={mut.isPending}>
          İptal et ve puanı iade et
        </Buton>
        <Buton type="button" gorunum="ikincil" disabled={mut.isPending} onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </form>
  );
}

const TELEFON_RAKAM = /^[0-9]{10,11}$/;

/**
 * Kâğıt veli onayı (Emre S11). Panelde kendi kısa adres alanları var (öğrenci uygulamasının formu içe aktarılmaz).
 * Sudo: sunucu `SUDO_GEREKLI` döndürünce şifre sorulur ve işlem kaldığı yerden yeniden denenir.
 */
function KagitOnayFormu({
  siparisId,
  onKapat,
  onBitti,
}: {
  siparisId: string;
  onKapat: () => void;
  onBitti: () => void;
}) {
  const { hataYakala, kapi } = useSudoKapisi(
    "Kâğıt veli onayını kaydetmek için şifrenizi yeniden girin.",
  );
  const [tarama, setTarama] = useState<File | null>(null);
  const [aliciAdi, setAliciAdi] = useState("");
  const [telefon, setTelefon] = useState("");
  const [il, setIl] = useState("");
  const [ilce, setIlce] = useState("");
  const [adres, setAdres] = useState("");
  const [postaKodu, setPostaKodu] = useState("");
  const [denendi, setDenendi] = useState(false);

  const mut = useMutation<unknown, unknown, { tarama: File; adres: SiparisAdresi }>({
    mutationFn: (girdi) => siparisKagitOnayi(siparisId, girdi),
    onError: (hata, girdi) => hataYakala(() => mut.mutate(girdi))(hata),
    onSuccess: () => {
      onBitti();
      onKapat();
    },
  });

  const telefonRakami = telefon.replace(/[\s()-]/g, "");
  const eksik = (v: string) =>
    denendi && v.trim() === "" ? "Bu alan boş bırakılamaz." : undefined;
  const telefonHatasi =
    denendi && telefon.trim() !== "" && !TELEFON_RAKAM.test(telefonRakami)
      ? "Telefonu 10 ya da 11 rakam olarak yazın."
      : eksik(telefon);

  return (
    <>
      {/* Sudo kapısı kendi <form>'u; iç içe form olmasın diye bu formun DIŞINDA. */}
      {kapi}
      <form
        className="border-notr-200 flex flex-col gap-3 border-t pt-3"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setDenendi(true);
          const bos = [aliciAdi, telefon, il, ilce, adres].some((v) => v.trim() === "");
          if (!tarama || bos || !TELEFON_RAKAM.test(telefonRakami) || mut.isPending) return;
          mut.mutate({
            tarama,
            adres: {
              aliciAdi: aliciAdi.trim(),
              telefon: telefonRakami,
              il: il.trim(),
              ilce: ilce.trim(),
              adres: adres.trim(),
              ...(postaKodu.trim() ? { postaKodu: postaKodu.trim() } : {}),
            },
          });
        }}
      >
        <p className="text-notr-700 text-sm">
          Velinin imzaladığı onayın taramasını ve onayda yazan teslimat adresini girin. Tarama
          dosyası teslim ya da iptalden 6 ay sonra silinir; “kâğıt onayı alındı” işareti ve
          onaylayan kişi kalır.
        </p>
        <Alan
          etiket="İmzalı veli onayının taraması"
          zorunlu
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          hata={denendi && !tarama ? "İmzalı taramayı seçin." : undefined}
          onChange={(e) => setTarama(e.target.files?.[0] ?? null)}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Alan
            etiket="Alıcı adı soyadı"
            zorunlu
            autoComplete="off"
            value={aliciAdi}
            hata={eksik(aliciAdi)}
            onChange={(e) => setAliciAdi(e.target.value)}
          />
          <Alan
            etiket="Telefon"
            zorunlu
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={telefon}
            hata={telefonHatasi}
            onChange={(e) => setTelefon(e.target.value)}
          />
          <Alan
            etiket="İl"
            zorunlu
            autoComplete="off"
            value={il}
            hata={eksik(il)}
            onChange={(e) => setIl(e.target.value)}
          />
          <Alan
            etiket="İlçe"
            zorunlu
            autoComplete="off"
            value={ilce}
            hata={eksik(ilce)}
            onChange={(e) => setIlce(e.target.value)}
          />
        </div>
        <MetinAlani
          etiket="Açık adres"
          zorunlu
          rows={2}
          maxLength={300}
          value={adres}
          hata={eksik(adres)}
          onChange={(e) => setAdres(e.target.value)}
        />
        <Alan
          etiket="Posta kodu"
          inputMode="numeric"
          autoComplete="off"
          value={postaKodu}
          onChange={(e) => setPostaKodu(e.target.value)}
        />
        {mut.isError && !sudoMu(mut.error) && (
          <p role="alert" className="text-hata-700 text-xs">
            {hataYazisi(mut.error, "Kâğıt onayı kaydedilemedi.")}
          </p>
        )}
        <div className="flex gap-2">
          <Buton type="submit" yukleniyor={mut.isPending}>
            Kâğıt onayıyla siparişi onayla
          </Buton>
          <Buton type="button" gorunum="ikincil" disabled={mut.isPending} onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </>
  );
}
